import { Injectable, inject } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { throwError } from 'rxjs'
import { DialogService } from './dialog.service'
import { Router } from '@angular/router'
import { Dialog } from '@angular/cdk/dialog'
import { ApiError } from '../types/apiError'
import { DialogOptions } from '../types/dialog'
import { TranslationService } from './translation.service'

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  private translationService = inject(TranslationService)

  handleError(httpErrorRes: HttpErrorResponse) {
    if (httpErrorRes.status === 0) {
      console.error('An error occurred:', httpErrorRes.error)
    } else {
      console.error(
        `Backend returned code ${httpErrorRes.status}, body was: `,
        httpErrorRes.error,
      )
    }

    // Normalize common backend error shapes to ApiError
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload = httpErrorRes?.error as any

    // Case 1: backend wraps error under `error` key
    if (payload && typeof payload === 'object' && payload.error) {
      const be = payload.error
      const apiErr: ApiError = {
        name: be?.name ?? 'UnknownError',
        message: be?.message,
        status: be?.status ?? httpErrorRes.status,
        details: be?.details,
      }
      return throwError(() => apiErr)
    }

    // Case 2: backend returns name/message at the top level
    if (payload && (payload.name || payload.message)) {
      const apiErr: ApiError = {
        name: payload.name ?? 'UnknownError',
        message: payload.message,
        status: payload.status ?? httpErrorRes.status,
        details: payload.details,
      }
      return throwError(() => apiErr)
    }

    // Fallback to a generic user-facing error
    return throwError(
      () => new Error('Something bad happened. Please try again later.'),
    )
  }

  /**
   * Gets a message for a corresponding defined API error. If one does not exist, a generic error message is returned.
   * @param {ApiError} err The API error object
   * @returns {string} error message
   */
  getApiErr(err: ApiError): string {
    const dynamicApiErrFormatter = this.dynamicApiErrMap.get(err.name)

    return (
      dynamicApiErrFormatter?.(err) ??
      this.staticApiErrMap.get(err.name) ??
      err.message ??
      'An error occurred. Please try again.'
    )
  }

  /**
   * Displays a dialog for an error thrown
   * @param {DialogService} dialogService The dialog service instance of the component where the error is thrown
   * @param {ApiError} err The error thrown
   * @param {Dialog} dialog The dialog reference
   * @param {DialogOptions} options The dialog options
   * @param {Router} router The router instance of the component where the error is thrown
   * @param {string} link Link to navigate to once the dialog is closed
   */
  handleErr(
    dialogService: DialogService,
    err: ApiError,
    dialog: Dialog,
    options?: DialogOptions,
    router?: Router,
    link?: string,
  ) {
    // TODO - Handle validation errors
    const apiErrMsg = this.getApiErr(err)

    dialogService.showDialog(dialog, 'Error', apiErrMsg, options, router, link)
  }

  private staticApiErrMap = new Map<string, string>([
    [
      'InternalServerError',
      this.translationService.getInternalServerErrorText(),
    ],
    ['EmailAlreadyExists', this.translationService.getEmailAlreadyExistsText()],
    [
      'PasswordTokenNotValid',
      this.translationService.getPasswordTokenNotValidText(),
    ],
    [
      'PasswordDoesNotMatchCriteria',
      this.translationService.getPasswordDoesNotMatchCriteriaText(),
    ],
    [
      'InvalidEmailOrPassword',
      this.translationService.getInvalidEmailOrPasswordText(),
    ],
    [
      'CompanyNotActive',
      this.translationService.getCompanyAssociatedWithUserNotActiveText(),
    ],
    ['InvalidAccessToken', this.translationService.getInvalidAccessTokenText()],
    ['SessionExpired', this.translationService.getSessionExpiredText()],
    [
      'InsufficientPermissions',
      this.translationService.getInsufficientPermissionsText(),
    ],
    ['ProductNotFound', this.translationService.getProductNotFoundText()],
    [
      'ProductDetailsRestricted',
      this.translationService.getProductDetailsRestrictedText(),
    ],
    [
      'CompanyWithIdentificationPhoneNumberOrEmailAlreadyExists',
      this.translationService.getCompanyWithIdentificationPhoneNumberOrEmailAlreadyExistsText(),
    ],
    [
      'CompanyDoesNotExist',
      this.translationService.getCompanyDoesNotExistText(),
    ],
    [
      'IngredientAlreadyExistsWithName',
      this.translationService.getIngredientAlreadyExistsWithNameText(),
    ],
    [
      'QuotesNotAllowed',
      'Single and double quotes are disallowed in search keywords.',
    ],
    [
      'ErrorRetrievingProductInfo',
      this.translationService.getFailedToGetProductDetailsError(),
    ],
    [
      'IngredientAlreadyExistsWithAlias',
      this.translationService.getIngredientAlreadyExistsWithAliasText(),
    ],
    [
      'IngredientAlreadyExistsWithCasNumbers',
      this.translationService.getIngredientAlreadyExistsWithCasNumberText(),
    ],
    [
      'RecycledContentPercentageError',
      this.translationService.getRecycledContentPercentageError(),
    ],
    ['PackageTypeError', this.translationService.getPackageTypeError()],
    [
      'PackageNotAllowedError',
      this.translationService.getPackageNotAllowedError(),
    ],
    [
      'InvalidPackageTypeError',
      this.translationService.getInvalidPackageTypeError(),
    ],
    ['InvalidProvinceError', this.translationService.getInvalidProvinceError()],
    [
      'InvalidRecyclabilityStatusError',
      this.translationService.getInvalidRecyclabilityStatusError(),
    ],
    [
      'RecyclabilityStatusMissingError',
      this.translationService.getRecyclabilityStatusMissingError(),
    ],
    [
      'ProvincialOrCountryRecyclabilityRequiredForPlasticProductsError',
      this.translationService.getProvincialOrCountryRecyclabilityRequiredForPlasticProductsError(),
    ],
    [
      'TemplateNameAlreadyExistsForCompany',
      this.translationService.getTemplateAlreadyExistsWithNameForCompanyError(),
    ],
    [
      'TemplateNameAlreadyExistsForCompany',
      this.translationService.getTemplateNameAlreadyExistsForCompanyText(),
    ],
    [
      'ProductImportLimitExceeded',
      this.translationService.getProductImportLimitExceededText(),
    ],
    [
      'NoFilesUploadedForProductImport',
      this.translationService.getNoFilesUploadedForProductImportText(),
    ],
    [
      'TemplateRequiredForProductImport',
      this.translationService.getTemplateRequiredForProductImportText(),
    ],
    [
      'ProductImportFileMissing',
      this.translationService.getProductImportFileMissingText(),
    ],
    [
      'ErrorOpeningImportCSV',
      this.translationService.getErrorOpeningImportCSVText(),
    ],
    ['ErrorParsingCSV', this.translationService.getErrorParsingCSVText()],
    [
      'CategoryCodeRequiredForAllProducts',
      this.translationService.getCategoryCodeRequiredForAllProductsText(),
    ],
    [
      'ProductsMissingFilesForImport',
      this.translationService.getProductsMissingFilesForImportText(),
    ],
    [
      'ErrorAssociatingProductsWithFiles',
      this.translationService.getErrorAssociatingProductsWithFilesText(),
    ],
    [
      'PrimaryArticleIdentifierRequiredForAllProducts',
      this.translationService.getPrimaryArticleIdentifierRequiredForAllProductsError(),
    ],
    [
      'DuplicatePrimaryIdentifiersFound',
      this.translationService.getDuplicatePrimaryIdentifiersFoundError(),
    ],
    ['InvalidMfaCode', this.translationService.getInvalidMfaCodeErrorText()],
    [
      'InternalServerError',
      this.translationService.getInternalServerErrorText(),
    ],
    [
      'MfaAlwaysRequiredForMaster',
      this.translationService.getMfaAlwaysRequiredForMasterText(),
    ],
    ['CannotChangeOwnMfa', this.translationService.getCannotChangeOwnMfaText()],
    [
      'CannotChangeMfaOutsideCompany',
      this.translationService.getCannotChangeMfaOutsideCompanyText(),
    ],
    [
      'CannotChangeMfaForAdmins',
      this.translationService.getCannotChangeMfaForAdminsText(),
    ],
    [
      'IngredientNameDuplicate',
      this.translationService.getIngredientNameDuplicateText(),
    ],
    [
      'IngredientOtherNameDuplicate',
      this.translationService.getIngredientOtherNameDuplicateText(),
    ],
    [
      'IngredientCASNumberDuplicate',
      this.translationService.getIngredientCASNumberDuplicateText(),
    ],
    ['ErrorParsingCSV', this.translationService.getErrorParsingCSVText()],
    ['ErrorMissingField', this.translationService.getErrorMissingFieldText()],
    [
      'ErrorCSVLimitExceeded',
      this.translationService.getErrorCSVLimitExceededText(),
    ],
    [
      'CannotCopyOwnProduct',
      this.translationService.getProductAlreadyOwnedErrorText(),
    ],
    [
      'CannotCopyACopiedProduct',
      this.translationService.getProductAlreadyCopiedErrorText(),
    ],
    [
      'IsPlasticPackagingMissing',
      this.translationService.getIsPlasticPackagingMissingText(),
    ],
    [
      'MissingFprTemplateColumns',
      this.translationService.getMissingFprTemplateColumnsText(),
    ],
    [
      'ProductsMissingForImport',
      this.translationService.getProductsMissingForImportText(),
    ],
    [
      'FprImportFileTooLarge',
      this.translationService.getFprImportFileTooLargeText(),
    ],
    [
      'ErrorDiscontinuedYearBeforeReportingYear',
      this.translationService.getDiscontinuedYearBeforeReportingYearErrorText(),
    ],
    [
      'DuplicateProductComponent',
      this.translationService.getDuplicateProductComponentsText(),
    ],
    [
      'PrimaryArticleIdentifierAlreadyExists',
      this.translationService.getPrimaryArticleIdentifierAlreadyExistsText(),
    ],
    [
      'InvalidCsvForTemplate',
      this.translationService.getInvalidCsvForTemplateText(),
    ],
    [
      'CannotUnshareIndirectComponent',
      this.translationService.getCannotUnshareIndirectProductText(),
    ],
  ])

  private dynamicApiErrMap = new Map<string, (err: ApiError) => string>([
    [
      'ProductPriceRequired',
      (err) => this.translationService.getImportValidationText(err.message),
    ],
    [
      'TemplateFieldNotFound',
      (err: ApiError) => {
        const missingFields = Array.isArray(err.details?.['missingFields'])
          ? err.details['missingFields'].filter(
              (field): field is string => typeof field === 'string',
            )
          : []

        return this.translationService.getTemplateFieldNotFoundError(
          missingFields,
        )
      },
    ],
  ])
}
